import { useCallback, useMemo, useState } from 'react';
import moment from 'moment';

import { GOLD_BADGE_LABEL, SILVER_BADGE_LABEL } from '../../src/services/badge';
import { Badge } from '../interfaces/users.interface';
import { WORKFLOW_STATUSES } from '../services/workflow';

type HideShowArchivedAlertWorkflowStatus = typeof WORKFLOW_STATUSES.REMEDIATION | typeof WORKFLOW_STATUSES.FINAL_COMPLETE;

export function useIsQuestionnaireArchived({
  archiveDate,
  badges,
  workflowStatus,
}: {
  archiveDate?: Date | null;
  badges?: Badge[] | number[] | undefined;
  workflowStatus?: string;
}) {
  const [dismissed, setDismissed] = useState(false);

  const isQuestionnaireArchived: boolean = useMemo(() => {
    if (!archiveDate || moment().isBefore(moment(archiveDate))) {
      return false;
    }

    return !badges?.some((badge) => {
      if (typeof badge === 'number') return false;
      return [GOLD_BADGE_LABEL, SILVER_BADGE_LABEL].includes(badge.title);
    });
  }, [archiveDate, badges]);

  const { REMEDIATION, FINAL_COMPLETE } = WORKFLOW_STATUSES;

  const showArchivedAlert = useMemo(() => {
    if (!isQuestionnaireArchived || dismissed || !workflowStatus) return false;

    return ![REMEDIATION, FINAL_COMPLETE].includes(
      workflowStatus as HideShowArchivedAlertWorkflowStatus,
    );
  }, [isQuestionnaireArchived, dismissed, workflowStatus, REMEDIATION, FINAL_COMPLETE]);

  const dismissArchivedAlert = useCallback(() => {
    setDismissed(true);
  }, []);

  return {
    isQuestionnaireArchived,
    showArchivedAlert,
    dismissArchivedAlert,
  };
}
